import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {X, ArrowRight} from 'lucide-react';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  onGuestCheckout?: () => void;
  currentLang: string;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin, onGuestCheckout, currentLang }) => {
  if (!isOpen) return null;

  const t = {
    zh: {
      welcome: "歡迎來到",
      subtitle: "開啟您的專業咖啡旅程",
      loginBtn: "使用 GOOGLE 帳號登入",
      guestCheckoutBtn: "訪客結帳",
      guestCheckoutHint: "無需註冊，快速完成購物",
      join: "加入 ONGU 會員俱樂部"
    },
    en: {
      welcome: "WELCOME TO",
      subtitle: "Start your professional coffee journey with us.",
      loginBtn: "LOGIN WITH GOOGLE",
      guestCheckoutBtn: "GUEST CHECKOUT",
      guestCheckoutHint: "Checkout without registration",
      join: "Join ONGU Member Club"
    }
  };

  const content = currentLang === 'zh' ? t.zh : t.en;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 m-auto w-full max-w-md h-fit p-1 z-[101]"
          >
            {/* Gradient Border Container */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-200 via-gray-400 to-gray-600 dark:from-gray-700 dark:via-gray-900 dark:to-black p-[1px]">
              <div className="bg-white dark:bg-black rounded-2xl p-8 md:p-10 relative overflow-hidden">
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 p-4 opacity-10">
                   <img src="https://static.lumi.new/f4/f41cec5bb6b72110e177830b0f994a38.png" alt="logo" className="w-32 grayscale" />
                </div>
                <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl"></div>

                {/* Close Button */}
                <button 
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-900 flex items-center justify-center mb-2 shadow-inner">
                    <span className="text-2xl">☕</span>
                  </div>
                  
                  <div className="space-y-2">
                    <h2 className="text-2xl font-light tracking-wider text-gray-900 dark:text-white">
                      {content.welcome} <span className="font-normal">ONGU</span>
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-light tracking-wide">
                      {content.subtitle}
                    </p>
                  </div>

                  <div className="w-full pt-4 space-y-3">
                    {/* Google 登入按鈕 */}
                    <button
                      onClick={onLogin}
                      className="group relative w-full flex items-center justify-center space-x-3 bg-black dark:bg-white text-white dark:text-black py-4 px-6 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                    >
                      {/* Button Shine Effect */}
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      
                      <span className="font-medium tracking-wide text-sm">{content.loginBtn}</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    {/* 訪客結帳按鈕 */}
                    {onGuestCheckout && (
                      <button
                        onClick={onGuestCheckout}
                        className="w-full flex items-center justify-center space-x-3 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-4 px-6 rounded-xl transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:scale-[1.02]"
                      >
                        <span className="font-medium tracking-wide text-sm">{content.guestCheckoutBtn}</span>
                      </button>
                    )}
                    
                    {onGuestCheckout && (
                      <p className="text-xs text-gray-400 dark:text-gray-600 text-center">
                        {content.guestCheckoutHint}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 dark:text-gray-600 font-mono">
                    {content.join}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LoginModal;
